/**
 * The Occupancy topline: one stacked bar per day, grey base plus a coloured segment per event
 * type, with the site's kitchen capacity as a dashed red line (POS2-1259 CP3).
 *
 * Stacked **by type** rather than by individual event, which is what the spec asks for and what
 * Recharts can express — five fixed series instead of a series per booking.
 */
import { Bar, BarChart, CartesianGrid, ReferenceArea, ReferenceLine, XAxis, YAxis } from "recharts";

import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import type { DayCovers, EventType } from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { BASE_COLOUR, isoWeekday, isWeekend, monthTotals, TYPE_COLOURS } from "@/lib/occupancy";
import { EVENT_TYPES } from "@/hooks/use-occupancy";
import { cn } from "@/lib/utils";

export type LegendKey = EventType | "base";

/** Literal keys, not `t(`occupancy.type.${x}`)`. `check:i18n` resolves keys statically, so a
 *  key built at runtime is invisible to it and the string silently escapes the catalogue. */
const LEGEND_LABEL_KEYS: Record<LegendKey, string> = {
  base: "occupancy.legend.base",
  wedding: "occupancy.type.wedding",
  conference: "occupancy.type.conference",
  function: "occupancy.type.function",
  banquet: "occupancy.type.banquet",
  general: "occupancy.type.general",
  other: "occupancy.type.other",
};

type Row = { date: string; label: string; base: number } & Record<EventType, number>;

function toRows(days: DayCovers[]): Row[] {
  return days.map((day) => {
    const byType = Object.fromEntries(EVENT_TYPES.map((type) => [type, 0])) as Record<
      EventType,
      number
    >;
    for (const event of day.events) byType[event.event_type] += event.covers;
    return {
      date: day.date,
      label: String(Number(day.date.slice(8))),
      base: day.base ?? 0,
      ...byType,
    };
  });
}

/** Contiguous weekend runs as [x1, x2] label pairs — the same shading Waste over time uses. */
function weekendBands(days: DayCovers[]): { x1: string; x2: string }[] {
  const bands: { x1: string; x2: string }[] = [];
  for (const day of days) {
    if (!isWeekend(day.date)) continue;
    const label = String(Number(day.date.slice(8)));
    const previous = bands[bands.length - 1];
    // Saturday opens a band, Sunday extends the one Saturday opened. A month starting on a
    // Sunday gets its own single-day band, which is why this checks the weekday rather than
    // assuming pairs.
    if (previous && isoWeekday(day.date) === 7 && Number(previous.x2) === Number(label) - 1) {
      previous.x2 = label;
    } else {
      bands.push({ x1: label, x2: label });
    }
  }
  return bands;
}

function Tooltip({
  active,
  payload,
  days,
}: {
  active?: boolean;
  payload?: unknown[];
  days: DayCovers[];
}) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const row = (payload[0] as { payload: Row }).payload;
  const day = days.find((d) => d.date === row.date);
  if (!day) return null;

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold">{day.date}</div>
      {day.base !== null && (
        <div className="flex justify-between gap-6 text-muted-foreground">
          <span>{t("occupancy.legend.base")}</span>
          <span className="tabular-nums text-foreground">{day.base}</span>
        </div>
      )}
      {day.events.map((event) => (
        <div key={event.id} className="flex justify-between gap-6 text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-[2px]"
              style={{ background: TYPE_COLOURS[event.event_type] }}
            />
            {event.name}
          </span>
          <span className="tabular-nums text-foreground">{event.covers}</span>
        </div>
      ))}
      {day.total !== null && (
        <div className="mt-1 flex justify-between gap-6 border-t border-border pt-1 font-semibold">
          <span>{t("occupancy.panel.total")}</span>
          <span className="tabular-nums">{day.total}</span>
        </div>
      )}
    </div>
  );
}

export function CoversChart({
  days,
  capacity,
  enabled,
  onToggle,
  onSelectDay,
}: {
  days: DayCovers[];
  capacity: number | null;
  enabled: Set<LegendKey>;
  onToggle: (key: LegendKey) => void;
  onSelectDay: (date: string) => void;
}) {
  const { t } = useTranslation();
  const rows = toRows(days);
  const totals = monthTotals(days);
  const bands = weekendBands(days);

  const legend: LegendKey[] = ["base", ...EVENT_TYPES];
  // Hoisted out of the JSX: `enabled.has("base")` inline reads to the i18next rule as a
  // user-facing literal, and it is a Set key.
  const showBase = enabled.has("base");
  const shownTypes = EVENT_TYPES.filter((type) => enabled.has(type));

  return (
    <div className="rounded-xl border border-border bg-background">
      <div className="flex flex-wrap items-start gap-4 px-5 pb-1 pt-4">
        <div>
          <h2 className="text-base font-bold tracking-tight">{t("occupancy.coversAndEvents")}</h2>
          <p className="mt-0.5 text-[13px] text-muted-foreground">{t("occupancy.chartSubtitle")}</p>
        </div>
        <div className="flex-1" />
        <dl className="flex gap-7 text-right">
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {t("occupancy.totals.total")}
            </dt>
            <dd className="text-lg font-bold tabular-nums">{totals.total}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {t("occupancy.totals.dailyAverage")}
            </dt>
            <dd className="text-lg font-bold tabular-nums">{totals.dailyAverage ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {t("occupancy.totals.busiestDay")}
            </dt>
            <dd className="text-lg font-bold tabular-nums">
              {totals.busiestDay ? totals.busiestDay.total : "—"}
            </dd>
          </div>
        </dl>
      </div>

      <ChartContainer config={{}} className="aspect-auto h-[260px] w-full px-2">
        <BarChart
          data={rows}
          margin={{ left: 12, right: 12, top: 8 }}
          onClick={(state) => {
            const row = state?.activePayload?.[0]?.payload as Row | undefined;
            if (row) onSelectDay(row.date);
          }}
        >
          <CartesianGrid vertical={false} />
          {bands.map((band) => (
            <ReferenceArea
              key={band.x1}
              x1={band.x1}
              x2={band.x2}
              fill="rgba(17,24,39,0.045)"
              fillOpacity={1}
              ifOverflow="extendDomain"
            />
          ))}
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} fontSize={11} />
          <YAxis tickLine={false} axisLine={false} width={38} fontSize={11} />
          <ChartTooltip content={<Tooltip days={days} />} cursor={false} />
          {showBase && (
            <Bar dataKey="base" stackId="covers" fill={BASE_COLOUR} radius={[0, 0, 0, 0]} />
          )}
          {shownTypes.map((type, index, shown) => (
            <Bar
              key={type}
              dataKey={type}
              stackId="covers"
              fill={TYPE_COLOURS[type]}
              radius={index === shown.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
            />
          ))}
          {capacity !== null && (
            <ReferenceLine
              y={capacity}
              stroke="#d92d20"
              strokeDasharray="4 4"
              strokeOpacity={0.55}
            />
          )}
        </BarChart>
      </ChartContainer>

      <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-5 py-3">
        {legend.map((key) => {
          const on = enabled.has(key);
          const colour = key === "base" ? BASE_COLOUR : TYPE_COLOURS[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggle(key)}
              aria-pressed={on}
              className={cn(
                "flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-[13px] font-medium transition-colors",
                on ? "text-foreground" : "text-muted-foreground opacity-55",
              )}
            >
              <span className="size-2 rounded-[2px]" style={{ background: colour }} />
              {t(LEGEND_LABEL_KEYS[key])}
            </button>
          );
        })}
        <span className="ml-auto text-xs text-muted-foreground">{t("occupancy.legend.hint")}</span>
      </div>
    </div>
  );
}
